import { createBrowserRouter, Navigate } from 'react-router';
import Landing from '../components/Landing/Landing';
import UnifiedLanding from '../components/UnifiedLanding/UnifiedLanding';
import Login from '../components/Login/Login';
import PageNotFound from '../components/PageNotFound/PageNotFound';
import UnauthorizedAccess from '../components/UnauthorizedAccess/UnauthorizedAccess';
import Register from '../components/Register/Register';
import MainLayout from '../components/MainLayout/MainLayout';
import ResetPassword from '../components/ResetPassword/ResetPassword';
import DrugSearchNew from '../pages/DrugSearch/DrugSearchNew';
import DrugInformation from '../pages/DrugSearch/DrugInformation/DrugInformation';
import CoverageVerification from '../pages/CoverageVerification/CoverageVerification';
import CoverageVerificationNew from '../pages/CoverageVerificationNew/CoverageVerification';
import Subscription from '../pages/Subscription/Subscription';
import PaymentStatus from '../pages/Subscription/PaymentStatus';
import ErrorPage from '../components/ErrorPage/ErrorPage';
import OrganisationSettings from '../pages/OrganizationSettings/OrganizationSettings';
import BusinessAdminRoute from './BusinessAdminRoute';
import ProfileSettings from '../pages/ProfileSettings/ProfileSettings';
import ProfileDetails from '../pages/ProfileSettings/ProfileDetails/ProfileDetails';
import ChangeSubscriptionPlan from '../pages/PlansPage/ChangeSubscriptionPlan';
import SuperAdminDashboard from '../pages/SuperAdmin/Dashboard/Dashboard';
import Organizations from '../pages/SuperAdmin/Dashboard/Organizations/Organizations';
import SuperAdminRoute from './SuperAdminRoute';
import PlanAndBillingContainer from '../pages/OrganizationSettings/PlanAndBilling/PlanBillingContainer';
import SupportFeedback from '../pages/SupportFeedback/SupportFeedback';
import OrganizationDetailsContainer from '../pages/OrganizationSettings/OrganisationDetails/OrganizationDetailsContainer';
import UserManagementContainer from '../pages/OrganizationSettings/UserManagement/UserManagementContainer';
import AuthGuard from './guards/AuthGuard';
import SubscriptionGuard from './guards/SubscriptionGuard';
import MobileGuard from './guards/MobileGuard';
import UserAndOrganizationActivity from '../pages/Analytics/UserAndOrganizationActivity/UserAndOrganizationActivity';
import CoverageVerificationModuleUsage from '../pages/Analytics/CoverageVerificationModuleUsage/CoverageVerificationModuleUsage';
import DrugSearchModuleUsage from '../pages/Analytics/DrugSearchModuleUsage/DrugSearchModuleUsage';
import SessionEngagementAnalytics from '../pages/Analytics/SessionEngagementAnalytic/SessionEngagementAnalytic';
import NewCoverageVerificationAnalytics from '../pages/Analytics/NewCoverageVerificationAnalytics/NewCoverageVerificationAnalytics';
import MobileNotSupported from '../pages/MobileNotSupported/MobileNotSupported';
import RetentionGrowth from '../pages/Analytics/InvestorMetrics/RetentionGrowth/RetentionGrowth';
import PracticePerformance from '../pages/Analytics/InvestorMetrics/PracticePerformance/PracticePerformance';
import WorkflowAdoption from '../pages/Analytics/InvestorMetrics/WorkflowAdoption/WorkflowAdoption';

const routesConfig = [
  { path: '/', element: <Login />, name: 'Login' },
  { path: '/register', element: <Register />, name: 'Register' },
  { path: '/mobile-not-supported', element: <MobileNotSupported />, name: 'MobileNotSupported' },
  {
    path: '/reset-password',
    element: <ResetPassword />,
    name: 'ResetPassword',
  },
  { path: '/error-page', element: <ErrorPage />, name: 'ErrorPage' },
  {
    path: '/settings',
    element: (
      <AuthGuard>
        <MobileGuard>
          <MainLayout />
        </MobileGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'organization',
        element: (
          <SubscriptionGuard>
            <BusinessAdminRoute>
              <OrganisationSettings />
            </BusinessAdminRoute>
          </SubscriptionGuard>
        ),
        children: [
          {
            path: 'details',
            element: <OrganizationDetailsContainer />,
            name: 'OrganizationDetailsContainer',
          },
          {
            path: 'users',
            element: <UserManagementContainer />,
            name: 'UserManagementContainer',
          },
          {
            path: 'billing',
            element: <PlanAndBillingContainer />,
            name: 'PlanAndBillingContainer',
          },
          {
            index: true,
            element: <Navigate to="details" replace />,
          },
        ],
      },
      {
        path: 'profile',
        element: <ProfileSettings />,
        children: [
          {
            path: 'details',
            element: <ProfileDetails />,
            name: 'ProfileDetails',
          },
          {
            index: true,
            element: <Navigate to="details" replace />,
          },
        ],
      },
      {
        path: 'change-plan',
        element: <ChangeSubscriptionPlan />,
        name: 'ChangeSubscriptionPlan',
      },
    ],
  },
  {
    path: '/subscription',
    element: (
      <AuthGuard>
        <SubscriptionGuard>
          <MainLayout />
        </SubscriptionGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Subscription />,
        name: 'Subscription',
      },
      {
        path: 'payment/callback',
        element: <PaymentStatus />,
        name: 'PaymentStatus',
      },
    ],
  },
  {
    path: '/landing',
    element: (
      <AuthGuard>
        <SubscriptionGuard>
          <MobileGuard>
            <UnifiedLanding />
          </MobileGuard>
        </SubscriptionGuard>
      </AuthGuard>
    ),
  },
  {
    path: '/drug-detail/:drugId',
    element: (
      <AuthGuard>
        <SubscriptionGuard>
          <MobileGuard>
            <DrugInformation />
          </MobileGuard>
        </SubscriptionGuard>
      </AuthGuard>
    ),
  },
  {
    path: '/landing-old',
    element: (
      <AuthGuard>
        <SubscriptionGuard>
          <MobileGuard>
            <MainLayout />
          </MobileGuard>
        </SubscriptionGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Landing />,
        name: 'Landing',
      },
      {
        path: 'drug-search',
        name: 'DrugSearch',
        children: [
          {
            index: true,
            element: <DrugSearchNew />,
            name: 'DrugSearch',
          },
          {
            path: ':drugId',
            element: <DrugInformation />,
            name: 'DrugInformation',
          },
        ],
      },
      {
        path: 'coverage-verification',
        name: 'CoverageVerification',
        children: [
          {
            index: true,
            element: (
              <SubscriptionGuard>
                <CoverageVerification />
              </SubscriptionGuard>
            ),
            name: 'CoverageVerification',
          },
        ],
      },
      {
        path: 'coverage-verification-new',
        name: 'CoverageVerificationNew',
        children: [
          {
            index: true,
            element: (
              <SubscriptionGuard>
                <CoverageVerificationNew />
              </SubscriptionGuard>
            ),
            name: 'CoverageVerificationNew',
          },
        ],
      },
    ],
  },
  {
    path: '/super-admin',
    element: (
      <AuthGuard>
        <SubscriptionGuard>
          <MobileGuard>
            <MainLayout />
          </MobileGuard>
        </SubscriptionGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/super-admin/analytics/user-activity" replace />,
      },
      {
        element: (
          <SuperAdminRoute>
            <SuperAdminDashboard />
          </SuperAdminRoute>
        ),
        children: [
          {
            path: 'organizations',
            children: [
              {
                index: true,
                element: <Organizations />,
              },
              {
                path: ':orgId',
                element: <OrganisationSettings />,
                children: [
                  {
                    path: 'details',
                    element: <OrganizationDetailsContainer />,
                    name: 'OrganizationDetailsContainer',
                  },
                  {
                    path: 'users',
                    element: <UserManagementContainer />,
                    name: 'UserManagementContainer',
                  },
                  {
                    path: 'billing',
                    element: <PlanAndBillingContainer />,
                    name: 'PlanAndBillingContainer',
                  },
                  {
                    index: true,
                    element: <Navigate to="details" replace />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'analytics',
        element: (
          <SuperAdminRoute>
            <SuperAdminDashboard />
          </SuperAdminRoute>
        ),
        children: [
          {
            path: 'user-activity',
            element: <UserAndOrganizationActivity />,
            name: 'UserActivityAnalytics',
          },
          {
            path: 'session-engagement',
            element: <SessionEngagementAnalytics />,
            name: 'SessionEngagementAnalytics',
          },
          {
            path: 'coverage-verification-usage',
            element: <CoverageVerificationModuleUsage />,
            name: 'CoverageVerificationUsageAnalytics',
          },
          {
            path: 'new-coverage-verification',
            element: <NewCoverageVerificationAnalytics />,
            name: 'NewCoverageVerificationAnalytics',
          },
          {
            path: 'drug-search-usage',
            element: <DrugSearchModuleUsage />,
            name: 'DrugSearchUsageAnalytics',
          },
          {
            path: 'retention-growth',
            element: <RetentionGrowth />,
            name: 'RetentionGrowth',
          },
          {
            path: 'practice-performance',
            element: <PracticePerformance />,
            name: 'PracticePerformance',
          },
          {
            path: 'workflow-adoption',
            element: <WorkflowAdoption />,
            name: 'WorkflowAdoption',
          },
        ],
      },
      {
        path: ':orgId/change-plan',
        element: <ChangeSubscriptionPlan />,
        name: 'ChangeSubscriptionPlan',
      },
    ],
  },
  {
    path: '/support',
    element: (
      <AuthGuard>
        <MobileGuard>
          <MainLayout />
        </MobileGuard>
      </AuthGuard>
    ),
    children: [
      // {
      //   path: 'faq',
      //   element: <Faq />,
      //   name: 'Faq',
      // },
      {
        path: 'feedback',
        element: <SupportFeedback />,
        name: 'SupportFeedback',
      },
    ],
  },
  {
    path: '/unauthorized',
    element: (
      <AuthGuard>
        <UnauthorizedAccess />
      </AuthGuard>
    ),
    name: 'UnauthorizedAccess',
  },
  { path: '*', element: <PageNotFound />, name: 'PageNotFound' },
];

const routes = createBrowserRouter(routesConfig);

export default routes;
